import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { EngineeringHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage } from '@shared/components';
import { EngineeringActions } from '@store/actions';
import {
  DDVSelectors,
  EngineeringSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';
import { Workflow } from './components';

import './styles.scss';

type EngineeringWorkflowProps = {
  isActive?: boolean;
};

export const EngineeringWorkflow: FC<EngineeringWorkflowProps> = ({
  isActive,
}) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    EngineeringSelectors.getTabLoadingState('workflow'),
  );
  const isDataLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('workflow'),
  );
  const projectType = useSelector(EngineeringSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringBrowse,
      Tab.Workflow,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'workflow',
          isLoading: true,
        }),
      );

      HttpService.getHttpRequests(EngineeringHttpService)
        .getEngineeringWorkflow(projectBundleID)
        .catch(() =>
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'workflow',
              hasError: true,
            }),
          ),
        );
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return <Spin size="large" />;
  }

  return (
    <EditablePage
      editableComponent={Workflow}
      isActive={isActive}
      canUserEdit={
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
    />
  );
};
